/**
 * Determines whether an element's text is truncated and, if so, attaches a
 * native `title` attribute so the browser shows a tooltip with the full text.
 *
 * Compares `offsetWidth` (visible width) against `scrollWidth` (full content
 * width) to detect overflow. When the content is clipped, the function sets
 * `element.title` to `title` (if provided) or falls back to `element.innerText`,
 * and returns `true`. When the content fits without clipping, any existing
 * `title` attribute is removed and the function returns `false`.
 *
 * Typical use: call this inside a `ResizeObserver` or after layout changes on
 * elements styled with `overflow: hidden; text-overflow: ellipsis` to
 * progressively enhance truncated text with a native tooltip.
 *
 * @param element - The DOM element to inspect. Returns `false` immediately for
 *   `null` or `undefined`.
 * @param title - Custom tooltip text. Defaults to the element's `innerText`
 *   when the content is truncated.
 * @returns `true` if the element is overflowing and a tooltip was set;
 *   `false` otherwise.
 *
 * @example
 * ```ts
 * const isTruncated = showTooltip(labelRef.current, 'Full company name');
 * ```
 */
export const showTooltip = (
    element: HTMLElement | null | undefined,
    title?: string,
): boolean => {
    if (element && element instanceof HTMLElement) {
        if (element.offsetWidth < element.scrollWidth) {
            element.setAttribute('title', title || element.innerText);
            return true;
        } else {
            element.removeAttribute('title');
            return false;
        }
    }

    return false;
};
